import { useTranslation } from 'react-i18next';
import { Button } from '@avanta/shared/components/ui/Button';
import { ButtonGroup } from '@avanta/shared/components/ui/ButtonGroup';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { type FilterValue, type TableFilterOption } from '@avanta/shared/types/table';
import { cn } from '@avanta/shared/utils/cn';

interface TableFilterButtonsProps<K extends FilterValue> {
  options: readonly TableFilterOption<K>[];
  selectedKey?: K;
  onChange: (key: K | undefined) => void;
  allLabel?: React.ReactNode;
}

const ACTIVE_BUTTON_CLASSNAME = 'bg-plum-100 border-plum-200 aria-pressed:font-medium';

export const TableFilterButtons = <K extends FilterValue>({
  options,
  selectedKey,
  onChange,
  allLabel,
}: TableFilterButtonsProps<K>): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.COMMON);

  return (
    <ButtonGroup>
      <Button
        type="button"
        variant="terciary"
        aria-pressed={selectedKey === undefined}
        onClick={() => onChange(undefined)}
        className={cn(selectedKey === undefined && ACTIVE_BUTTON_CLASSNAME)}
      >
        {allLabel ?? t('Common.all')}
      </Button>
      {options.map(option => (
        <Button
          key={String(option.key)}
          variant="terciary"
          aria-pressed={selectedKey === option.key}
          onClick={() => onChange(option.key)}
          className={cn('gap-2', selectedKey === option.key && ACTIVE_BUTTON_CLASSNAME)}
        >
          {option.label}
        </Button>
      ))}
    </ButtonGroup>
  );
};
